import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, Loader2, ChevronDown, ChevronUp, RefreshCw, Calendar,
  DollarSign, AlertTriangle, FileText,
} from "lucide-react";

/**
 * DocSummary, per-document AI summary trigger + display.
 *
 * Behaviour:
 *  • If doc.doc_type === "photo": render nothing (caller should hide).
 *  • If doc.ai_summary is set: show an expandable "Compass's read" card
 *    with overview, dates, money amounts, concerns. Refresh button costs
 *    another AI query.
 *  • Else: show a single subtle "Summarise with Compass (1 query)" button.
 *
 * Costs exactly 1 AI query against the user's monthly+daily allowance
 * (or burns a boost-pack query if monthly is gone). 402 errors surface
 * through the global QuotaErrorHandler toast.
 */
export default function DocSummary({ doc, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(doc.ai_summary || null);

  if (doc.doc_type === "photo") return null;
  if ((doc.mime_type || "").startsWith("image/") && doc.doc_type !== "screenshot" && doc.doc_type !== "notice") {
    // Hide on raw images that weren't classified as a letter/notice/screenshot ,
    // matches the "not photos" intent.
  }

  const run = async (refresh = false) => {
    setBusy(true);
    try {
      const url = refresh
        ? `/documents/${doc.id}/summarise?refresh=true`
        : `/documents/${doc.id}/summarise`;
      const { data } = await api.post(url);
      setSummary(data.summary);
      setOpen(true);
      if (!data.free && !data.cached) {
        toast.success("Summary ready, 1 query used.");
      }
      onUpdate?.({ ...doc, ai_summary: data.summary });
    } catch (e) {
      // 402 is handled globally by QuotaErrorHandler; only show toast on other errors
      if (e.response?.status !== 402) {
        toast.error(e.response?.data?.detail || "Couldn't summarise this document.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (!summary) {
    return (
      <button
        onClick={() => run(false)}
        disabled={busy}
        className="mt-2 text-[12px] inline-flex items-center gap-1.5 text-[#1B6C62] hover:underline disabled:opacity-60"
        data-testid={`doc-summarise-${doc.id}`}
      >
        {busy ? <><Loader2 size={11} strokeWidth={1.5} className="animate-spin" /> Reading…</> : <><Sparkles size={11} strokeWidth={1.5} /> Summarise with Compass (1 AI query)</>}
      </button>
    );
  }

  const isFallback = summary.model === "n/a";

  return (
    <div className="mt-3 bg-[#FAF6E8] border border-[#C9A961]/40" data-testid={`doc-summary-${doc.id}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-[#F4EDD7] transition-colors"
        data-testid={`doc-summary-toggle-${doc.id}`}
      >
        <span className="inline-flex items-center gap-1.5 text-[12.5px] text-[#0D2B52] font-medium">
          <Sparkles size={12} strokeWidth={1.5} className="text-[#8A6D3B]" />
          Compass&apos;s read
          {summary.doc_kind && summary.doc_kind !== "other" && (
            <span className="text-[10px] font-mono text-[#8A6D3B] uppercase ml-1 bg-white/70 px-1.5 py-0.5">{summary.doc_kind.replace(/_/g, " ")}</span>
          )}
        </span>
        {open ? <ChevronUp size={13} strokeWidth={1.5} /> : <ChevronDown size={13} strokeWidth={1.5} />}
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-[#C9A961]/30" data-testid={`doc-summary-body-${doc.id}`}>
          {/* Overview */}
          <p className="text-[13px] text-[#1A1A1A] leading-relaxed font-serif">
            {summary.overview || "(no overview generated)"}
          </p>

          {/* Key dates */}
          {Array.isArray(summary.key_dates) && summary.key_dates.length > 0 && (
            <div data-testid={`doc-summary-dates-${doc.id}`}>
              <div className="label-mono text-[10px] text-[#8A6D3B] mb-1 flex items-center gap-1">
                <Calendar size={10} strokeWidth={1.5} /> Key dates
              </div>
              <ul className="space-y-1 text-[12px] text-[#1A1A1A]">
                {summary.key_dates.map((k, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-mono text-[#0D2B52] shrink-0">{k.date || k.when}</span>
                    <span className="text-[#5a6b7a]">, {k.what || k.event}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Money amounts */}
          {Array.isArray(summary.money_amounts) && summary.money_amounts.length > 0 && (
            <div data-testid={`doc-summary-money-${doc.id}`}>
              <div className="label-mono text-[10px] text-[#8A6D3B] mb-1 flex items-center gap-1">
                <DollarSign size={10} strokeWidth={1.5} /> Money mentioned
              </div>
              <ul className="space-y-1 text-[12px] text-[#1A1A1A]">
                {summary.money_amounts.map((m, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="font-mono text-[#0D2B52] shrink-0">{m.amount}</span>
                    <span className="text-[#5a6b7a]">, {m.what || m.context}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Concerns */}
          {Array.isArray(summary.concerns) && summary.concerns.length > 0 && (
            <div data-testid={`doc-summary-concerns-${doc.id}`}>
              <div className="label-mono text-[10px] text-[#B35D4C] mb-1 flex items-center gap-1">
                <AlertTriangle size={10} strokeWidth={1.5} /> Worth a closer look
              </div>
              <ul className="space-y-1 text-[12px] text-[#1A1A1A] list-disc list-inside marker:text-[#B35D4C]">
                {summary.concerns.map((c, i) => <li key={i}>{c}</li>)}
              </ul>
            </div>
          )}

          {/* Footer */}
          <div className="pt-2 border-t border-[#C9A961]/30 flex items-center justify-between text-[10px] font-mono text-[#8A6D3B]">
            <span>
              {summary.generated_at && (<><FileText size={9} className="inline -mt-0.5 mr-1" />Generated {new Date(summary.generated_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}</>)}
            </span>
            {!isFallback && (
              <button
                onClick={() => run(true)}
                disabled={busy}
                className="inline-flex items-center gap-1 hover:underline disabled:opacity-60"
                title="Generate a fresh summary, costs 1 AI query"
                data-testid={`doc-summary-refresh-${doc.id}`}
              >
                {busy ? <Loader2 size={9} className="animate-spin" /> : <RefreshCw size={9} strokeWidth={1.75} />}
                Refresh
              </button>
            )}
          </div>
          <p className="text-[10px] text-[#6B7280] italic">
            Compass&apos;s read is an assistant summary, not legal advice. Always review the original.
          </p>
        </div>
      )}
    </div>
  );
}
